import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ShieldAlert, Receipt, ExternalLink, Eye, AlertCircle, Clock } from "lucide-react";
import { 
  AlertDialog, 
  AlertDialogContent, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogAction
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";

interface PricingPlan {
  plan_id: string;
  plan_name: string;
  original_price: number;
  discount: {
    type: 'fixed' | 'percentage';
    value: number;
    amount: number;
  } | null;
  price_after_discount: number;
  tax_rate: number;
  tax_amount: number;
  final_amount: number;
  duration: number;
  type: string;
}

interface ProductInfo {
  id: string;
  name: string;
  features: string | null;
  description: string | null;
  logo?: string;
  product_screen?: string;
}

interface PricingData {
  product: ProductInfo;
  plans: PricingPlan[];
}

interface PublicPlanSelectionProps {
  pricingData: PricingData;
  schoolId: string;
  userId: string | null;
  productName: string;
  token?: string | null;
}

interface SubscriptionStatus {
  planId: string;
  status: string;
  endDate?: string | null;
}

export function PublicPlanSelection({ 
  pricingData, 
  schoolId, 
  userId,
  productName,
  token
}: PublicPlanSelectionProps) {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [activeSubscriptions, setActiveSubscriptions] = useState<SubscriptionStatus[]>([]);
  const [checkingSubscriptions, setCheckingSubscriptions] = useState(true);
  const { toast } = useToast();

  const sortedPlans = [...pricingData.plans].sort((a, b) => {
    const orderMap: Record<string, number> = {
      'Yearly': 1,
      'Monthly': 2,
      'One-Time': 3
    };
    
    const orderA = orderMap[a.plan_name] || 99;
    const orderB = orderMap[b.plan_name] || 99;
    
    return orderA - orderB;
  });

  const yearlyPlan = sortedPlans.find(plan => plan.plan_name === 'Yearly');
  
  const otherPlans = sortedPlans.filter(plan => plan.plan_name !== 'Yearly');

  useEffect(() => {
    const checkExistingSubscriptions = async () => {
      setCheckingSubscriptions(true);
      try {
        const { data, error } = await supabase.functions.invoke('check-subscription-status', {
          body: { 
            productId: pricingData.product.id,
            schoolId
          }
        });
        
        if (error) {
          console.error("Error checking subscription status:", error);
          return;
        }
        
        if (data.success && (data.active || data.status === 'pending')) {
          console.log("Found existing subscription:", data);
          
          const { data: subscriptionDetails, error: subError } = await supabase
            .from('subscriptions')
            .select(`
              plan_id,
              status,
              end_date
            `)
            .eq('id', data.subscription_id)
            .single();
            
          if (subError) {
            console.error("Error fetching subscription details:", subError);
            const planStatuses = pricingData.plans.map(plan => ({
              planId: plan.plan_id,
              status: data.status || 'active',
              endDate: data.end_date
            }));
            
            setActiveSubscriptions(planStatuses);
            return;
          }
          
          if (subscriptionDetails) {
            const planStatuses = pricingData.plans.map(plan => ({
              planId: plan.plan_id,
              status: subscriptionDetails.status || data.status || 'active',
              endDate: subscriptionDetails.end_date || data.end_date
            }));
            
            setActiveSubscriptions(planStatuses);
          }
        }
      } catch (error) {
        console.error("Failed to check subscription status:", error);
      } finally {
        setCheckingSubscriptions(false);
      }
    };
    
    if (pricingData?.product?.id && schoolId) {
      checkExistingSubscriptions();
    }
  }, [pricingData?.product?.id, schoolId]);

  const isPlanSubscribed = (planId: string): SubscriptionStatus | undefined => {
    if (activeSubscriptions.length > 0) {
      const specificPlan = activeSubscriptions.find(sub => sub.planId === planId);
      if (specificPlan) return specificPlan;
      
      return activeSubscriptions[0];
    }
    return undefined;
  };

  const showConfirmationDialog = (planId: string) => {
    const plan = pricingData.plans.find(p => p.plan_id === planId);
    if (!plan) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Selected plan not found"
      });
      return;
    }
    
    setSelectedPlanId(planId);
    setSelectedPlan(plan);
    setConfirmDialogOpen(true);
  };

  const handleSubscribe = async () => {
    try {
      if (!selectedPlanId || !selectedPlan) {
        throw new Error("No plan selected");
      }
      
      setLoading(true);
      setConfirmDialogOpen(false);

      const userEmail = "product@infradigital.io";

      const subscriptionData: any = {
        productId: pricingData.product.id,
        planId: selectedPlanId,
        schoolId,
        userEmail
      };

      if (token) {
        subscriptionData.token = token;
      }

      console.log("Creating subscription with:", subscriptionData);

      try {
        const { data, error } = await supabase.functions.invoke('create-public-subscription', {
          body: subscriptionData
        });

        if (error) {
          console.error("Error from edge function:", error);
          throw new Error(error.message || 'Failed to create subscription');
        }
        
        if (!data || !data.success) {
          console.error("Unsuccessful response:", data);
          throw new Error((data && data.error) || 'Failed to create subscription');
        }

        console.log("Subscription created successfully:", data);
        
        if (data.data.bill && data.data.bill.school_id) {
          setInvoiceUrl(`https://www.infradigital.net/bills?biller_code=60000&bill_key=${data.data.bill.school_id}`);
        }
        
        setSuccessDialogOpen(true);
      } catch (error: any) {
        throw error;
      }
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      
      if (error.message && (
          error.message.includes('SCHOOL_NOT_REGISTERED') || 
          error.message.includes('School not registered')
      )) {
        const schoolIdMatch = error.message.match(/(\d+)/);
        const errorSchoolId = schoolIdMatch ? schoolIdMatch[0] : schoolId;
        
        setErrorMessage(`Sekolah dengan Kode IDN ${errorSchoolId} belum terdaftar di Kasflow. Silakan hubungi administrator untuk mendaftarkan sekolah Anda.`);
      } else if (error.message && error.message.includes('Unauthorized')) {
        setErrorMessage("Anda tidak memiliki akses untuk melakukan langganan. Silakan gunakan tautan yang valid dari administrator.");
      } else if (error.message && error.message.includes('non-2xx status code')) {
        setErrorMessage("Terjadi kesalahan pada server. Silakan coba lagi nanti atau hubungi administrator.");
      } else if (error.message && error.message.includes('foreign key constraint')) {
        setErrorMessage("Pengguna tidak terdaftar di sistem. Silakan hubungi administrator untuk mendaftarkan pengguna terlebih dahulu.");
      } else {
        setErrorMessage(error.message || "Failed to create subscription. Please try again.");
      }
      
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const formatTaxInfo = (taxRate: number) => {
    return `Termasuk PPN ${(taxRate * 100).toFixed(0)}%`;
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderSubscriptionStatus = (planId: string) => {
    const subscription = isPlanSubscribed(planId);
    if (!subscription) return null;
    
    const isTerminatedButActive = 
      subscription.status === 'terminated' && 
      subscription.endDate && 
      new Date(subscription.endDate) > new Date();
      
    const isExpired = 
      subscription.endDate && 
      new Date(subscription.endDate) <= new Date();
      
    if (isTerminatedButActive) {
      return (
        <div className="mt-2 text-xs py-1 px-2 bg-orange-50 border border-orange-200 rounded text-orange-700 flex items-center">
          <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>Masih Aktif sampai {formatDate(subscription.endDate)}</span>
        </div>
      );
    } else if (isExpired) {
      return null;
    } else if (subscription.status === 'pending') {
      return (
        <div className="mt-2 text-xs py-1 px-2 bg-amber-50 border border-amber-200 rounded text-amber-700 flex items-center">
          <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>Menunggu pembayaran</span>
        </div>
      );
    } else if (subscription.status === 'active') {
      return (
        <div className="mt-2 text-xs py-1 px-2 bg-green-50 border border-green-200 rounded text-green-700 flex items-center">
          <CheckCircle2 className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>Sudah berlangganan</span>
        </div>
      );
    } else {
      return (
        <div className="mt-2 text-xs py-1 px-2 bg-amber-50 border border-amber-200 rounded text-amber-700 flex items-center">
          <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>
            {subscription.status === 'active' 
              ? 'Sudah berlangganan' 
              : subscription.status === 'pending' 
                ? 'Menunggu pembayaran' 
                : subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
          </span>
        </div>
      );
    }
  };

  const isButtonDisabled = (planId: string): boolean => {
    if (loading || checkingSubscriptions) return true;
    
    const subscription = isPlanSubscribed(planId);
    if (!subscription) return false;
    
    if (subscription.endDate && new Date(subscription.endDate) <= new Date()) {
      return false;
    }
    
    if (subscription.status === 'terminated' && 
        subscription.endDate && 
        new Date(subscription.endDate) > new Date()) {
      return true;
    }
    
    return true;
  };

  const getButtonText = (planId: string): string => {
    const subscription = isPlanSubscribed(planId);
    
    if (subscription) {
      if (subscription.endDate && new Date(subscription.endDate) <= new Date()) {
        return 'Berlangganan Kembali';
      }
      
      if (subscription.status === 'terminated' && 
          subscription.endDate && 
          new Date(subscription.endDate) > new Date()) {
        return 'Sudah Berlangganan';
      }
      
      return 'Sudah Berlangganan';
    }
    
    return 'Berlangganan';
  };

  return (
    <>
      <div className="space-y-6">
        {yearlyPlan && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-semibold">{yearlyPlan.plan_name}</span>
                  {yearlyPlan.discount && yearlyPlan.discount.amount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">
                      Diskon {yearlyPlan.discount.type === 'percentage' ? `${yearlyPlan.discount.value}%` : `Rp ${yearlyPlan.discount.amount.toLocaleString('id-ID')}`}
                    </span>
                  )}
                </div>
                <div className="text-4xl font-bold mb-1">
                  Rp {(yearlyPlan.final_amount).toLocaleString('id-ID')}
                </div>
                {yearlyPlan.discount && yearlyPlan.discount.amount > 0 && (
                  <div className="text-sm text-gray-500 line-through mb-1">
                    Rp {yearlyPlan.original_price.toLocaleString('id-ID')}
                  </div>
                )}
                <div className="text-gray-600">
                  Durasi: {yearlyPlan.duration} hari
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatTaxInfo(yearlyPlan.tax_rate)}
                </div>
                {renderSubscriptionStatus(yearlyPlan.plan_id)}
              </div>
              <Button 
                className="rounded-lg px-8 bg-[#008169] hover:bg-[#006e59]"
                onClick={() => showConfirmationDialog(yearlyPlan.plan_id)}
                disabled={isButtonDisabled(yearlyPlan.plan_id)}
              >
                {loading && selectedPlanId === yearlyPlan.plan_id ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  getButtonText(yearlyPlan.plan_id)
                )}
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {otherPlans.map(plan => (
            <div key={plan.plan_id} className="bg-white rounded-xl p-6 shadow-sm">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{plan.plan_name}</span>
                  {plan.discount && plan.discount.amount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">
                      Diskon {plan.discount.type === 'percentage' ? `${plan.discount.value}%` : `Rp ${plan.discount.amount.toLocaleString('id-ID')}`}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-bold">
                  Rp {(plan.final_amount).toLocaleString('id-ID')}
                </div>
                {plan.discount && plan.discount.amount > 0 && (
                  <div className="text-sm text-gray-500 line-through">
                    Rp {plan.original_price.toLocaleString('id-ID')}
                  </div>
                )}
                <div className="text-gray-600">
                  Durasi: {plan.duration} hari
                </div>
                <div className="text-xs text-gray-500">
                  {formatTaxInfo(plan.tax_rate)}
                </div>
                {renderSubscriptionStatus(plan.plan_id)}
                <Button 
                  className="w-full rounded-lg bg-[#008169] hover:bg-[#006e59]"
                  onClick={() => showConfirmationDialog(plan.plan_id)}
                  disabled={isButtonDisabled(plan.plan_id)}
                >
                  {loading && selectedPlanId === plan.plan_id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    getButtonText(plan.plan_id)
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Langganan</DialogTitle>
            <DialogDescription>
              Anda akan berlangganan {pricingData.product.name} dengan paket berikut:
            </DialogDescription>
          </DialogHeader>
          {selectedPlan && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <div className="font-medium text-lg">{pricingData.product.name}</div>
                  <div className="text-sm text-gray-500">{selectedPlan.plan_name}</div>
                </div>
                
                <div className="space-y-2 text-sm border-t pt-3">
                  <div className="flex justify-between">
                    <span>Harga</span>
                    <span>Rp {selectedPlan.original_price.toLocaleString('id-ID')}</span>
                  </div>
                  
                  {selectedPlan.discount && selectedPlan.discount.amount > 0 && (
                    <div className="flex justify-between text-red-500">
                      <span>Diskon ({selectedPlan.discount.type === 'percentage' ? `${selectedPlan.discount.value}%` : 'Fixed'})</span>
                      <span>- Rp {selectedPlan.discount.amount.toLocaleString('id-ID')}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span>Harga setelah diskon</span>
                    <span>Rp {selectedPlan.price_after_discount.toLocaleString('id-ID')}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span>PPN ({(selectedPlan.tax_rate * 100).toFixed(0)}%)</span>
                    <span>Rp {selectedPlan.tax_amount.toLocaleString('id-ID')}</span>
                  </div>
                  
                  <div className="flex justify-between font-semibold border-t pt-2">
                    <span>Total</span>
                    <span>Rp {selectedPlan.final_amount.toLocaleString('id-ID')}</span>
                  </div>
                </div>
                
                <div className="mt-4 text-xs text-gray-500">
                  <div>Durasi: {selectedPlan.duration} hari</div>
                  <div>Kode Sekolah: {schoolId}</div>
                </div>
              </div>
              
              <div className="text-sm text-gray-500 mb-4">
                <p>Dengan mengklik tombol "Bayar", Anda setuju untuk berlangganan layanan ini sesuai dengan syarat dan ketentuan yang berlaku.</p>
              </div>
            </div>
          )}
          <DialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Batal
            </Button>
            <Button 
              type="button" 
              onClick={handleSubscribe}
              className="bg-[#008169] hover:bg-[#006e59]"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Receipt className="mr-2 h-4 w-4" />
                  Bayar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">Subscription Created Successfully</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="py-4">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-center mb-4">
                  Langganan Anda telah berhasil dibuat. Anda akan menerima email dengan detail faktur dalam waktu singkat.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  {invoiceUrl && (
                    <Button 
                      onClick={() => window.open(invoiceUrl, '_blank')}
                      className="bg-[#008169] hover:bg-[#006e59] flex items-center"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Lihat Tagihan Subscribe
                    </Button>
                  )}
                  {token && (
                    <Button 
                      onClick={() => window.open(`/subscriptions/public?schoolId=${schoolId}&token=${token}`, '_blank')}
                      className="bg-[#008169] hover:bg-[#006e59] flex items-center"
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Lihat Subscription
                    </Button>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Subscription Error</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="py-4">
                <ShieldAlert className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-center">{errorMessage}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrorDialogOpen(false)}>
              Try Again
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
